import React, { useState } from 'react';
import { Tabs } from 'antd';
import {
  InfoCircleOutlined,
  AlertOutlined,
} from '@ant-design/icons';
import CandidateRow from './CandidateRow';
import WhyNotSearch from './WhyNotSearch';
import contractsModule from './contracts';
import './aiCrew.css';

const contracts = contractsModule.default || contractsModule;
const { TIER } = contracts;

/**
 * EmployeeList - Displays Recommended and Other Eligible candidate tabs with structured
 * column headers, client-side backfill notices, shortfall banner, and WhyNotSearch.
 * Blocked workers remain excluded by the API and are intentionally not exposed to clients.
 */
export default function EmployeeList({
  candidates = [],
  selectedIds = new Set(),
  onToggle,
  headcount = 0,
  backfillNote = null,
}) {
  const [activeTab, setActiveTab] = useState('recommended');

  const recommendedCandidates = candidates.filter(
    (c) => c.tier === (TIER?.RECOMMENDED || 'RECOMMENDED')
  );
  const eligibleCandidates = candidates.filter(
    (c) => c.tier === (TIER?.ELIGIBLE || 'ELIGIBLE')
  );

  const isShortfall = candidates.length < headcount;

  const renderCandidateList = (list) => {
    if (!list || list.length === 0) {
      return (
        <div style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 8 }}>
          No candidates in this tier
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Table Subheader */}
        <div className="ai-crew-roster-header">
          <span className="ai-crew-col-worker">Worker & Trade</span>
          <span className="ai-crew-col-signals">Match Signals & Precedents</span>
        </div>

        {list.map((candidate) => (
          <CandidateRow
            key={candidate.employeeId}
            candidate={candidate}
            selected={selectedIds.has(candidate.employeeId)}
            onToggle={onToggle}
          />
        ))}
      </div>
    );
  };

  const tabItems = [
    {
      key: 'recommended',
      label: `Recommended (${recommendedCandidates.length})`,
      children: (
        <div>
          {renderCandidateList(recommendedCandidates)}
          <WhyNotSearch
            candidates={candidates}
            selectedIds={selectedIds}
            onAddEmployee={onToggle}
          />
        </div>
      ),
    },
    {
      key: 'eligible',
      label: `Other Eligible (${eligibleCandidates.length})`,
      children: (
        <div>
          {renderCandidateList(eligibleCandidates)}
        </div>
      ),
    },
  ];

  return (
    <div className="ai-crew-employee-list-container" data-testid="ai-crew-employee-list">
      {/* Shortfall warning banner if total eligible candidates < headcount */}
      {isShortfall && (
        <div className="ai-crew-shortfall-banner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <AlertOutlined style={{ color: '#d97706', fontSize: 16 }} />
            <span>
              <strong>Staffing Alert:</strong> Only {candidates.length} conflict-free workers found for this shift (requested {headcount}).
            </span>
          </div>
        </div>
      )}

      {/* Backfill interaction feedback */}
      {backfillNote && (
        <div className="ai-crew-backfill-notice">
          <InfoCircleOutlined style={{ fontSize: 14 }} />
          <span>{backfillNote}</span>
        </div>
      )}

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ marginTop: 4 }}
      />
    </div>
  );
}
